import type { Rate } from './reporting.js';

// SMS Lead Bot report — the RENDERING side of the honesty rules. PURE.
//
// ⚠️ The aggregation decides what is unmeasured or unreportable; this decides
// how that looks. Both halves are needed: a core that returns null and a
// template that prints `?? 0` would undo the whole thing in one character.

/** ⚠️ The one unmeasured marker. Never "0", never "0%", never blank. */
export const UNMEASURED = '—';

/**
 * A rate, with its denominator always attached.
 *
 * ⚠️ THE DENOMINATOR IS NOT OPTIONAL DETAIL. "60%" and "60% (3 of 5)" are
 * different claims, and on a trial dashboard only the second one is honest.
 * Below the sample floor the percentage is withheld entirely and the counts
 * stand on their own — a reader can see "2 of 3" and draw their own conclusion,
 * which is exactly what a confident-looking 67% would have denied them.
 */
export function formatRate(rate: Rate): string {
  if (rate.denominator === 0) return UNMEASURED;
  const counts = `${rate.numerator} of ${rate.denominator}`;
  if (rate.value === null) return counts;
  return `${Math.round(rate.value * 100)}% (${counts})`;
}

/** The big figure for a metric card — the percentage, or the raw counts. */
export function rateHeadline(rate: Rate): string {
  if (rate.denominator === 0) return UNMEASURED;
  if (rate.value === null) return `${rate.numerator}/${rate.denominator}`;
  return `${Math.round(rate.value * 100)}%`;
}

/** The line under it: what the figure is over, and whether it is trustworthy. */
export function rateCaption(rate: Rate): string {
  if (rate.denominator === 0) return 'nothing measured yet';
  if (rate.value === null) {
    return `${rate.numerator} of ${rate.denominator} — too few to give a percentage`;
  }
  return `${rate.numerator} of ${rate.denominator}`;
}

/** ⚠️ Null is a dash. A 0 would be a measurement nobody took. */
export function formatCount(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? value.toLocaleString()
    : UNMEASURED;
}

export function formatDecimal(value: number | null | undefined, places = 1): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? value.toFixed(places)
    : UNMEASURED;
}

/** A duration in the units a person would say it in. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return UNMEASURED;
  if (seconds < 90) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 90) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours.toFixed(1)} hr`;
  return `${Math.round(hours / 24)} days`;
}

/**
 * ⚠️ Reads as a proportion of tokens, not money. A dollar figure would need
 * per-model pricing baked in here, and a stale price presented to the pound is
 * worse than an honest token count — the rates change and nothing would tell us.
 */
export function formatTokens(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return UNMEASURED;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(Math.round(value));
}
